import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CommonUtil } from "../../../utils/CommonUtil";
import { Constants } from "../../Languages/Constants";
import InputField from "../../../Components/InputField";
import SelectField from "../../../Components/SelectField";
import { NetworkManager } from "../../../Components/NetworkManager";
import ReactSelectField from "../../../Components/ReactSelectField";
import ToastManager from "../../../Components/ToastManager";
import Confirm from "../../../Components/confirm";

const ClaimsForm = ({mode, onClose, initialData}: any) => {
  const saasOrgId = CommonUtil.getSaasOrgId();
  const [carrierList, setCarrierList] = useState<any>([]);
  const [states, setStates] = useState<any>([]);

  const onLoginError = (err: any) => {
    console.log(err);
  };

  useEffect(() => {
    getBrokerCareerList();
  }, []);

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      formik.setValues({
        claimReason: initialData.claimReason || '',
        carrierName: initialData.carrierName ? { label: initialData.carrierName, value: initialData.carrierId } : null,
        otherCarrier: initialData.carrierName === "Others" ? initialData.otherCarrier : '',
        description: initialData.claimDescription || '',
        claimObj: initialData.claimType || '',
        countryObj: initialData.country || '',
        stateObj: initialData.city ? { label: initialData.city, value: initialData.city } : null,
        claimAmount: initialData.claimAmount || '',
        taxAmount: initialData.taxAmount || '',
      });
    }
  }, [mode, initialData]);

  const getBrokerCareerList = () => {
    const orgUrl = `api/v2/carrier-broker/all/?saasOrgId=${saasOrgId}`;
    NetworkManager.sendJsonRequest(orgUrl, "", "", "GET", true, onBrokerCareerList, onLoginError);
  };

  const onBrokerCareerList = (dataObj: any) => {
    if (dataObj) {
      var restData = JSON.parse(dataObj);
      if (restData) {
        const filteredData = restData.filter((item: any) => item?.status?.toLowerCase() === "active");
        const updatedData = filteredData.map((item: any) => ({
          value: item.carrierBrokerId,
          label: item.name,
        }));
        setCarrierList(updatedData);
      }
    }
  };

  const validationSchema = Yup.object({
    claimReason: Yup.string().required("Claim reason is required"),
    carrierName: Yup.object()
      .nullable()
      .required("Carrier is required"),
    otherCarrier: Yup.string().when("carrierName", (carrierName: any, schema: any) => {
      if (carrierName[0]?.label === "Others") {
        return schema.required("Please enter Other Carrier");
      }
      return schema.nullable();
    }),
    description: Yup.string().required("Description is required"),
    claimObj: Yup.string().required("Claim Type is required"),
    countryObj: Yup.string().required("Country is required"),
    stateObj: Yup.object()
      .nullable()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
      .required("State is required"),
    claimAmount: Yup.number()
      .typeError("Claim amount must be a number")
      .required("Claim amount is required"),
    taxAmount: Yup.number()
      .typeError("Tax amount must be a number")
      .required("Tax amount is required"),
  });

  const formik = useFormik({
    initialValues: {
      claimReason: "",
      carrierName: null as { value: string; label: string } | null,
      otherCarrier: "",
      description: "",
      claimObj: "",
      countryObj: "",
      stateObj: null as { label: string; value: string } | null,
      claimAmount: "",
      taxAmount: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      onSubmitForm(values);
    },
  });
  
  const onSubmitForm = (values: any) => {
    const method = mode === "edit" ? "PUT" : "POST";
    const claimAPI =
      mode === "edit"
        ? `api/claim/${initialData?.claimId}?isForInterchange=true`
        : `api/claim/?isForInterchange=true`;

    const payload = {
      claimAmount: values.claimAmount,
      claimDateString: CommonUtil.getReservationDateString(new Date()),
      claimDescription: values.description,
      carrierName:
        values.carrierName?.label === "Others"
          ? values.otherCarrier
          : values.carrierName?.label,
      carrierId:
        values.carrierName?.label === "Others" ? "" : values.carrierName?.value,
      claimReason: values.claimReason,
      claimType: CommonUtil.CLAIM_TYPES.find((item) => item.key === values.claimObj)?.value || "",
      country: values.countryObj,
      city: values.stateObj?.label,
      inspectionDocumentId: CommonUtil.getInterchangeDocumentId(),
      saasOrgId: saasOrgId,
      taxAmount: values.taxAmount,
      recipient: "renter",
    };
    NetworkManager.sendJsonRequest(claimAPI, payload, "", method, true, onSaveClaims, onLoginError);
  }

  const onSaveClaims = (dataObj: any) => {
    if (dataObj) {
      const restData = CommonUtil.parseData(dataObj);
      if (restData && !restData.errorMessage) {
        if (mode === "edit") {
          ToastManager.showToast("Claim Updated Successfully", "Info");
        } else {
          ToastManager.showToast("Claim Created Successfully", "Info");
        }
        onClose();
        formik.resetForm();
      }else{
        ToastManager.showToast(restData.errorMessage, "Error");
      }
    }
  };
  
  useEffect(() => {
    if(formik.values.countryObj === CommonUtil.CANADA) {
      const mappedProvinces = CommonUtil.canadaProvinces.map((item: any) => ({
        label: item.Value,
        value: item.Key,
      }));
      setStates(mappedProvinces);
    } else {
      const mappedStates = CommonUtil.usStates.map((item: any) => ({
        label: item.Value,
        value: item.Key,
      }));
      setStates(mappedStates);
    }
  },[formik.values.countryObj]);

  const handleCountryChange = (value: any) => {
    formik.setFieldValue("countryObj", value);
    formik.setFieldValue("stateObj", null); 
  };

  const onClickDeleteClaim = (id: any) =>{
    if (mode === "edit") {
      Confirm.show("Confirm", "Do you want to delete this claim?", [{
        label: "Yes",
        callback: function() {
          let removeAPI: string = `api/claim/${id}?isForInterchange=true`;
          NetworkManager.sendJsonRequest(removeAPI, "", "", "DELETE", true, onDeleteClaim, onLoginError);
        },
      },
      {label: "No"}]);
    }
  };
  
  const onDeleteClaim = (dataObj: any) => {
    if (dataObj == "success") {
      console.log(dataObj, "Claim deleted successfully");
      onClose();
      formik.resetForm();
      ToastManager.showToast("Claim deleted successfully", "Info");
    }else{
      const restData = CommonUtil.parseData(dataObj);
      ToastManager.showToast(restData.errorMessage, "Error");
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <div>
        <div className="p-0 mt-2">
          <div className="row mt-1 mb-4">
              <div className="col-4">
                  <InputField
                    type="text"
                    name="claimReason"
                    value={formik.values.claimReason}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={CommonUtil.getMessageText(Constants.CLAIM_REASON, "Claim Reason")}                
                  />
                  {formik.touched.claimReason && formik.errors.claimReason && (
                    <small className="ms-2 text-danger fw-700">{formik.errors.claimReason}</small>
                  )}
              </div>
              <div className="col-4">
                <SelectField
                  name="claimObj"
                  placeholder={CommonUtil.getMessageText(Constants.CLAIM_TYPE, "Claim Type")}
                  onChange={formik.handleChange}
                  value={formik.values.claimObj}
                  onBlur={formik.handleBlur}
                >
                  <option value="" disabled>Select</option>
                  {CommonUtil.CLAIM_TYPES?.map((item :any, i: any) => {
                    return (
                      <option key={i} value={item.key}>
                        {item.value}
                      </option>
                    );
                  })}
                </SelectField>
                  {formik.touched.claimObj && formik.errors.claimObj && (
                    <small className="ms-2 text-danger fw-700">{formik.errors.claimObj}</small>
                  )}
              </div>
              <div className="col-4">
                <ReactSelectField
                  placeholder={CommonUtil.getMessageText(Constants.CARRIER_NAME, "Carrier Name")}
                  name="carrierName"
                  options={[...carrierList]}
                  value={formik.values.carrierName}
                  onChange={(value: any) => {
                    formik.setFieldValue("carrierName", value);
                    if (value?.label === "Others") {
                      formik.setFieldValue("otherCarrier", "");
                    }
                  }}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.carrierName && formik.errors.carrierName && (
                  <small className="ms-2 text-danger fw-700">{formik.errors.carrierName}</small>
                )}
              </div>
            </div>
            {/* <div className="row mt-2 mb-4">
              <div className="col-6">
                <ReactSelectField
                  placeholder={CommonUtil.getMessageText(Constants.CARRIER_NAME, "Carrier Name")}
                  name="carrierName"
                  options={[...carrierList]}
                  value={formik.values.carrierName}
                  onChange={(value: any) => {
                    formik.setFieldValue("carrierName", value);
                    if (value?.label === "Others") {
                      formik.setFieldValue("otherCarrier", "");
                    }
                  }}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.carrierName && formik.errors.carrierName && (
                  <small className="ms-2 text-danger fw-700">{formik.errors.carrierName}</small>
                )}
              </div>
              {formik.values.carrierName && formik.values.carrierName.label === "Others" && 
                (<div className="col-6">
                  <InputField
                    type="text"
                    name="otherCarrier"
                    value={formik.values.otherCarrier}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Other Carrier"
                  />
                  {formik.touched.otherCarrier && formik.errors.otherCarrier && (
                    <small className="ms-2 text-danger fw-700">{formik.errors.otherCarrier}</small>
                  )}
                </div>)}
          </div> */}
          <div className="row mb-4">
            <div className="col-12">
              <InputField rows={2} name="description"
                  placeholder={CommonUtil.getMessageText(Constants.DESCRIPTION, "Description")}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                />
                {formik.touched.description && formik.errors.description && (
                  <small className="ms-2 text-danger fw-700">{formik.errors.description}</small>
                )}
            </div>
          </div>
          <div className="row mb-4">
            <div className="col-6">
              <SelectField
                name="countryObj"
                placeholder={CommonUtil.getMessageText(Constants.COUNTRY, "Country")}
                value={formik.values.countryObj || ""}
                onChange={(e: any) => handleCountryChange(e.target.value)}
                onBlur={formik.handleBlur}
              >
                <option value="" disabled>Select</option>
                {CommonUtil.countryList?.map((item :any, i: any) => {
                  return (
                    <option key={i} value={item.Value}>
                      {item.Key}
                    </option>
                  );
                })}
              </SelectField>
                {formik.touched.countryObj && formik.errors.countryObj && (
                  <small className="ms-2 text-danger fw-700">{formik.errors.countryObj}</small>
                )}
            </div>
            <div className="col-6">
                <ReactSelectField
                  name="stateObj"
                  placeholder={CommonUtil.getMessageText(Constants.STATE, "State")}
                  value={formik.values.stateObj || null}
                  options={states}
                  onChange={(value: any) => formik.setFieldValue("stateObj", value)}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.stateObj && formik.errors.stateObj && (
                  <small className="ms-2 text-danger fw-700">{formik.errors.stateObj}</small>
                )}
            </div>
          </div>
          <div className="row mb-4">
            <div className="col-6">
              <InputField
                type="text"
                name="claimAmount"
                value={formik.values.claimAmount}
                onChange={(e: any) => {
                  CommonUtil.handleNumbersDecimalInput({
                    event: e,
                    ref: null,
                    setState: (val: string) =>
                      formik.setFieldValue("claimAmount", val),
                  });
                }}
                onKeyPress={CommonUtil.allowNumbersDecimal}
                className="form-control"
                placeholder={CommonUtil.getMessageText(Constants.CLAIM_AMOUNT, "Claim Amount")}
              />
              {formik.touched.claimAmount && formik.errors.claimAmount ? 
                <small className="ms-2 text-danger fw-700">{formik.errors.claimAmount}</small> :
                <small className="ms-2 fst-italic fw-700">Enter claim amount</small>
              }
            </div>
            <div className="col-6">
              <InputField
                type="text"
                name="taxAmount"
                value={formik.values.taxAmount}
                onChange={(e :any) => {
                  CommonUtil.handleNumbersDecimalInput({
                    event: e,
                    ref: null,
                    setState: (val: string) => formik.setFieldValue("taxAmount", val),
                  });
                }}
                onKeyPress={CommonUtil.allowNumbersDecimal}
                className="form-control"
                placeholder={CommonUtil.getMessageText(Constants.TAX_AMOUNT, "Tax Amount")}
              />
              {formik.touched.taxAmount && formik.errors.taxAmount && (
                <small className="ms-2 text-danger fw-700">{formik.errors.taxAmount}</small>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-between mt-3">
        <div className="">
        {mode === "edit" && (
          <button type="button" className="btn btn-outline-danger" onClick={() => onClickDeleteClaim(initialData.claimId)}>
            {CommonUtil.getMessageText(Constants.DELETE, "Delete")}
          </button>
        )}
        </div>
        <div className="">
          <button
            type="button"
            className="btn btn-outline-dark me-2"
            data-bs-dismiss="modal"
            onClick={() => onClose()}
          >
            {CommonUtil.getMessageText(Constants.CLOSE, "Cancel")}
          </button>
          <button type="submit" className="btn btn-success">
            {CommonUtil.getMessageText(Constants.SUBMIT, "Submit")}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ClaimsForm;
